import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crosshair, RefreshCw, ChevronDown, ChevronRight, AlertTriangle,
  RotateCcw, GitBranch, FileSearch, Eye, ArrowUpRight, DollarSign, Landmark,
  Bitcoin, TrendingUp, User, Users, Building2, Receipt, Building, HelpCircle,
  ArrowDownWideNarrow, ArrowUpNarrowWide, ArrowLeftRight, FileText,
} from 'lucide-react';
import {
  useMoneyTrail, useRunMoneyTrail,
  type MoneyTrailLead, type MoneyTrailEvidenceTxn,
} from '../../hooks/useTransactions';
import { formatTxnDate } from '../../utils/dates';
import { formatAccountName } from '../../utils/accounts';
import { money } from '../../lib/money';
import type { DiscoveryLead } from '../../hooks/useStatements';
import {
  Button, Card, DataTable, EmptyState, IconButton, MetricCard, Skeleton, Tag,
  ToolbarChip, ToolbarGroup, type ColumnDef,
} from '../ui';
import SourcePageViewer from '../SourcePageViewer';
import MissingAccountsTab from '../MissingAccountsTab';

interface Props {
  caseId: string;
}

interface ViewerState {
  statementId: number;
  pageNumber: number;
  sourceFile: string;
}

type SortDir = 'desc' | 'asc';
type FilterKey = 'all' | 'high' | 'missing' | 'internal';

const TYPE_LABEL: Record<string, string> = {
  CRYPTO: 'Crypto', UNDISCLOSED_BANK: 'Outside bank account', BROKERAGE: 'Brokerage',
  INDIVIDUAL: 'Individual', BUSINESS: 'Business', GOVERNMENT_TAX: 'Tax / government',
  BENIGN_INSTITUTION: 'Known institution', PARTY: 'Case party', UNKNOWN: 'Unidentified recipient',
};

function fmt(n: number | null | undefined) {
  return money(Math.abs(n ?? 0), { whole: true });
}

function netOf(lead: MoneyTrailLead) {
  return Math.abs(lead.net_amount ?? lead.total_amount ?? 0);
}

/** A lead is a "missing account" (the Discovery → Missing Accounts tab sense)
 *  when the destination is an undisclosed bank/card account with a demandable
 *  last-4 and no statements on file — NOT an internal transfer to one of the
 *  client's own disclosed accounts, and NOT a wire to a person/business that
 *  merely names an account number. Computed deterministically on the backend
 *  (`is_missing_account`) so it never depends on the AI's recipient-type guess. */
function isMissingBankAccount(lead: MoneyTrailLead) {
  return lead.is_missing_account === true;
}

/** Whether the destination names an account number we have no statements for.
 *  Drives the informational chip — true for individual/business wires too. */
function hasUndisclosedNumber(lead: MoneyTrailLead) {
  return !!lead.last4 && (lead.reasons ?? []).some((r) => /no statements/i.test(r));
}

/** An internal transfer to one of the client's own disclosed accounts. */
function isInternalTransfer(lead: MoneyTrailLead) {
  return lead.disclosed_internal === true;
}

/** Money moved INTO an own account during months its statements are missing —
 *  the high-value concealment/statement-gap signal. */
function hasGapAnomaly(lead: MoneyTrailLead) {
  return lead.anomaly === 'into_missing_statements' && (lead.into_missing_amount ?? 0) > 0;
}

function fmtMonths(yms: string[] | undefined): string {
  if (!yms || !yms.length) return '';
  const labels = yms.map((ym) => {
    const [y, m] = ym.split('-');
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  });
  return labels.length <= 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/** Reshape a money-trail missing-account lead into the DiscoveryLead the shared
 *  MissingAccountsTab component renders, so the "Missing accounts" view reuses
 *  that exact UI (stats header, expandable per-account table, source viewer,
 *  Copy-for-Discovery) rather than a parallel design. */
function toDiscoveryLead(lead: MoneyTrailLead): DiscoveryLead {
  const acctType = lead.account_type || 'Account';
  return {
    account_type: acctType,
    last4: lead.last4 || '',
    label: lead.last4 ? `${acctType} ...${lead.last4}` : (lead.destination_name || lead.destination_label),
    total_amount: lead.total_amount,
    transfer_count: lead.transfer_count,
    first_date: lead.first_date,
    last_date: lead.last_date,
    transactions: (lead.transactions ?? []).map((t) => ({
      date: t.date,
      description: t.description || '',
      amount: t.amount ?? 0,
      source_bank_label: t.bank_label || '',
      statement_id: t.statement_id ?? null,
      source_page: t.source_page ?? null,
      source_file: t.source_file || '',
      raw_text: '',
      confidence_score: null,
    })),
  };
}

function TypeIcon({ type, size = 16 }: { type?: string | null; size?: number }) {
  const cls = 'text-clay-600 shrink-0';
  switch (type) {
    case 'CRYPTO': return <Bitcoin size={size} className={cls} />;
    case 'UNDISCLOSED_BANK': return <Landmark size={size} className={cls} />;
    case 'BROKERAGE': return <TrendingUp size={size} className={cls} />;
    case 'INDIVIDUAL': return <User size={size} className={cls} />;
    case 'PARTY': return <Users size={size} className={cls} />;
    case 'BUSINESS': return <Building2 size={size} className={cls} />;
    case 'GOVERNMENT_TAX': return <Receipt size={size} className={cls} />;
    case 'BENIGN_INSTITUTION': return <Building size={size} className={cls} />;
    default: return <HelpCircle size={size} className={cls} />;
  }
}

function FlagChips({ lead }: { lead: MoneyTrailLead }) {
  const chips: React.ReactNode[] = [];
  if (lead.round_trip)
    chips.push(<Tag key="rt" tone="info" icon={RotateCcw}>Money later returned</Tag>);
  if (lead.layered)
    chips.push(
      <Tag key="ly" tone="warn" icon={GitBranch}>
        Funds arrived from {lead.origin_account ? formatAccountName(lead.origin_account) : 'another account'} first
      </Tag>
    );
  if (lead.new_onset_after_separation)
    chips.push(<Tag key="sep" tone="danger">Started after separation</Tag>);
  if (hasUndisclosedNumber(lead))
    chips.push(<Tag key="ms" tone="warn">No statements on file (…{lead.last4})</Tag>);
  if (hasGapAnomaly(lead))
    chips.push(
      <Tag key="gap" tone="danger" icon={AlertTriangle}>
        {fmt(lead.into_missing_amount)} moved in during a statement gap
      </Tag>
    );
  if (!chips.length) return null;
  return <div className="flex items-center gap-2 flex-wrap px-5 pt-3">{chips}</div>;
}

function LeadRow({ lead, onViewSource, onRequestStatements }: {
  lead: MoneyTrailLead;
  onViewSource: (v: ViewerState) => void;
  onRequestStatements: (last4: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // A transfer to one of the client's OWN disclosed accounts is internal, not an
  // outside account — never label it by the AI's (disclosed-status-blind) type.
  const isInternal = !!lead.disclosed_internal;
  // Never surface the raw transaction description: clean name → account …last4
  // → a by-type generic. destination_label is kept only as evidence, not a title.
  const isUnknownType = !lead.destination_type || lead.destination_type === 'UNKNOWN';
  const typeLabel = isInternal
    ? 'Internal transfer · own account'
    : lead.destination_type ? (TYPE_LABEL[lead.destination_type] ?? lead.destination_type) : null;
  const name = lead.destination_name
    || (lead.last4 ? `Account …${lead.last4}` : (typeLabel ?? 'Undisclosed recipient'));
  // "Unidentified" as a type sub-label is noise — suppress it (the recipient
  // name, e.g. "Venmo", already carries what we know).
  const showType = !!typeLabel && typeLabel !== name && (isInternal || !isUnknownType);
  const returned = lead.returned_amount ?? 0;
  const period =
    lead.first_date && lead.last_date && lead.first_date !== lead.last_date
      ? `${formatTxnDate(lead.first_date)} – ${formatTxnDate(lead.last_date)}`
      : lead.first_date ? formatTxnDate(lead.first_date) : '—';

  function toggle(e: React.MouseEvent | React.KeyboardEvent) {
    if ('key' in e && e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    setOpen((v) => !v);
  }

  return (
    <div className="rounded-lg border border-clay-200 overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={toggle}
        className="w-full flex items-center gap-4 px-5 py-4 bg-clay-50 hover:bg-clay-100 transition-colors cursor-pointer select-none"
      >
        <div className="shrink-0 text-clay-500">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        {isInternal
          ? <ArrowLeftRight size={16} className="text-clay-600 shrink-0" />
          : <TypeIcon type={lead.destination_type} />}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-clay-900 text-sm leading-tight truncate">{name}</p>
            {showType && <span className="text-xs text-clay-600 shrink-0">{typeLabel}</span>}
          </div>
        </div>

        <div className="hidden md:block text-right shrink-0 w-40">
          <p className="text-xs text-clay-600">{period}</p>
        </div>

        <div className="text-right shrink-0 w-32">
          <p className="font-bold text-danger text-sm tabular-nums">{fmt(netOf(lead))}</p>
          <p className="text-xs text-clay-600">
            {lead.transfer_count} transfer{lead.transfer_count !== 1 ? 's' : ''}
            {returned > 0 ? ' · net' : ''}
          </p>
        </div>
      </div>

      {open && (
        <div className="border-t border-clay-200 bg-surface-card">
          <FlagChips lead={lead} />
          {lead.narrative && (
            <p className="px-5 py-3 text-xs text-clay-700 leading-relaxed bg-clay-50 border-b border-clay-100 mt-3">
              {lead.narrative}
            </p>
          )}
          {hasGapAnomaly(lead) && lead.last4 && (
            <div className="px-5 py-3 border-b border-clay-100">
              <Button
                size="sm"
                icon={FileText}
                onClick={(e) => { e.stopPropagation(); onRequestStatements(lead.last4!); }}
              >
                Request the …{lead.last4} statements
              </Button>
              <span className="ml-2 text-xs text-ink-faint">
                Drafts a discovery demand for {fmtMonths(lead.into_missing_months)}.
              </span>
            </div>
          )}
          {returned > 0 && (
            <p className="px-5 pt-3 text-xs text-ink-faint">
              Gross out {fmt(lead.total_amount)} · {fmt(returned)} later returned · net {fmt(netOf(lead))}
            </p>
          )}
          <EvidenceTable txns={lead.transactions ?? []} onViewSource={onViewSource} />
        </div>
      )}
    </div>
  );
}

function EvidenceTable({ txns, onViewSource }: { txns: MoneyTrailEvidenceTxn[]; onViewSource: (v: ViewerState) => void }) {
  if (!txns.length) {
    return <p className="px-5 py-4 text-xs text-ink-faint">No itemized transactions on this lead.</p>;
  }
  const columns: ColumnDef<MoneyTrailEvidenceTxn>[] = [
    {
      id: 'date',
      header: 'Date',
      width: 'w-28',
      edge: true,
      cell: (t) => (
        <span className="text-xs text-ink-soft whitespace-nowrap">
          {t.date ? formatTxnDate(t.date) : '—'}
        </span>
      ),
    },
    {
      id: 'account',
      header: 'Source account',
      width: 'w-40',
      cell: (t) => (
        <span className="text-xs text-ink-soft">
          {t.bank_label ? formatAccountName(t.bank_label) : '—'}
        </span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      width: 'flex',
      minWidth: 'min-w-48',
      cell: (t) => <span className="text-xs text-ink-soft">{t.description || '—'}</span>,
    },
    {
      id: 'source',
      header: 'Source',
      width: 'w-24',
      stopPropagation: true,
      cell: (t) => {
        const hasSource = !!(t.statement_id && t.source_page);
        return hasSource ? (
          <Button
            variant="ghost"
            size="sm"
            icon={Eye}
            onClick={() => onViewSource({ statementId: t.statement_id!, pageNumber: t.source_page!, sourceFile: t.source_file || '' })}
            title={t.source_file ? `${t.source_file} (p.${t.source_page})` : `Page ${t.source_page}`}
          >
            p.{t.source_page}
          </Button>
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        );
      },
    },
    {
      id: 'amount',
      header: 'Amount',
      width: 'w-28',
      numeric: true,
      edge: true,
      cell: (t) => {
        const neg = (t.amount ?? 0) < 0;
        return (
          <span className={`text-xs font-semibold whitespace-nowrap ${neg ? 'text-danger' : 'text-green-700'}`}>
            {money(t.amount, { whole: true, signed: true })}
          </span>
        );
      },
    },
  ];
  return (
    <DataTable
      caption="Evidence transactions for this lead"
      rows={txns}
      columns={columns}
      rowKey={(t) => t.id}
      density="compact"
      minWidth={640}
      stickyHeader={false}
      className="rounded-none border-0 border-t border-border-subtle"
    />
  );
}

function StatHeader({ leads, income }: { leads: MoneyTrailLead[]; income?: number }) {
  const totalNet = leads.reduce((s, l) => s + netOf(l), 0);
  const totalTransfers = leads.reduce((s, l) => s + (l.transfer_count || 0), 0);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <MetricCard
        label="Net Unaccounted"
        value={<span className="text-danger">{fmt(totalNet)}</span>}
        icon={<DollarSign size={16} />}
      />
      <MetricCard label="Leads" value={String(leads.length)} icon={<Crosshair size={16} />} />
      <MetricCard label="Transfers" value={String(totalTransfers)} icon={<ArrowUpRight size={16} />} />
      <MetricCard label="Client Income / mo" value={income ? fmt(income) : '—'} icon={<Landmark size={16} />} />
    </div>
  );
}

export default function MoneyTrailView({ caseId }: Props) {
  const navigate = useNavigate();
  // Hand the lawyer straight to a discovery demand for the flagged account's
  // missing statements — the demand-letter tab already auto-detects the gap; the
  // focus param checks/scrolls/highlights it so the action carries over.
  const requestStatements = (last4s: string) =>
    navigate(`/cases/${caseId}/drafts/letters?focus_last4=${encodeURIComponent(last4s)}`);
  const { data, isLoading } = useMoneyTrail(caseId);
  const runMutation = useRunMoneyTrail(caseId);
  const [viewer, setViewer] = useState<ViewerState | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [filter, setFilter] = useState<FilterKey>('all');

  const allLeads = data?.leads ?? [];
  const hasRun = !!data;
  const sep = data?.separation_date;

  const missingCount = useMemo(() => allLeads.filter(isMissingBankAccount).length, [allLeads]);
  const highCount = useMemo(() => allLeads.filter((l) => l.tier === 'high').length, [allLeads]);
  const internalCount = useMemo(() => allLeads.filter(isInternalTransfer).length, [allLeads]);

  // If the active filter's tab is no longer shown (e.g. a re-run drops the
  // High-priority count to 0, or Missing accounts to 0), fall back to All leads
  // so the user isn't stranded on an empty, orphaned filter.
  useEffect(() => {
    if (filter === 'high' && highCount === 0) setFilter('all');
    else if (filter === 'missing' && missingCount === 0) setFilter('all');
    else if (filter === 'internal' && internalCount === 0) setFilter('all');
  }, [filter, highCount, missingCount, internalCount]);

  const leads = useMemo(() => {
    let out = allLeads;
    if (filter === 'high') out = out.filter((l) => l.tier === 'high');
    else if (filter === 'missing') out = out.filter(isMissingBankAccount);
    else if (filter === 'internal') out = out.filter(isInternalTransfer);
    const sign = sortDir === 'desc' ? 1 : -1;
    const sorted = [...out].sort((a, b) => sign * (netOf(b) - netOf(a)));
    // In the internal view, float the gap-anomaly leads (the real signal) to the top.
    if (filter === 'internal') {
      sorted.sort((a, b) => Number(hasGapAnomaly(b)) - Number(hasGapAnomaly(a)));
    }
    return sorted;
  }, [allLeads, filter, sortDir]);

  // Missing-account leads reshaped for the shared Discovery MissingAccountsTab UI,
  // sorted by amount in the same direction as the rest of the tab.
  const missingDiscoveryLeads = useMemo(() => {
    const sign = sortDir === 'desc' ? 1 : -1;
    return allLeads
      .filter(isMissingBankAccount)
      .sort((a, b) => sign * (Math.abs(b.total_amount) - Math.abs(a.total_amount)))
      .map(toDiscoveryLead);
  }, [allLeads, sortDir]);

  // "High priority" only earns a slot when something actually clears the bar
  // (score ≥ 70 — realistically post-separation drains). Otherwise it's a
  // permanent "0" chip that adds noise, so it's hidden.
  const FILTERS: { key: FilterKey; label: string; count: number }[] = [
    { key: 'all', label: 'All leads', count: allLeads.length },
    ...(highCount > 0 ? [{ key: 'high' as FilterKey, label: 'High priority', count: highCount }] : []),
    { key: 'missing', label: 'Missing accounts', count: missingCount },
    // Internal transfers between the client's own accounts — quiet by default;
    // only worth a tab when there are some (and the gap-anomaly banner flags the
    // ones that matter).
    ...(internalCount > 0 ? [{ key: 'internal' as FilterKey, label: 'Internal transfers', count: internalCount }] : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="t-card text-clay-900 flex items-center gap-2">
            <Crosshair size={20} className="text-clay-700" /> Money Trail
          </h2>
          <p className="text-sm text-ink-faint mt-1 max-w-3xl">
            Money that left the disclosed accounts as a transfer with no matching deposit anywhere
            we can see — ranked relative to this client's income{sep ? ' and weighted toward activity after the separation date' : ''}.
            These are leads for attorney review, not confirmed findings. Run this once the statement
            dump is fully uploaded.
          </p>
        </div>
        <Button
          variant="secondary"
          icon={RefreshCw}
          onClick={() => runMutation.mutate()}
          loading={runMutation.isPending}
          className="shrink-0"
        >
          {hasRun ? 'Re-run trace' : 'Trace hidden assets'}
        </Button>
      </div>

      {runMutation.isError && (
        <div className="flex items-center gap-2 px-4 py-2 bg-danger-soft border border-danger rounded-lg text-sm text-danger">
          <AlertTriangle size={16} /> Trace failed. Please try again.
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton variant="rect" className="h-12 w-full rounded-lg" />
          <Skeleton variant="rect" className="h-16 w-full rounded-lg" />
          <Skeleton variant="rect" className="h-16 w-full rounded-lg" />
        </div>
      ) : !hasRun ? (
        <Card padding="none">
          <EmptyState
            icon={FileSearch}
            title="No money-trail analysis yet"
            description={<>Upload all the statements you have, then click “Trace hidden assets.”</>}
          />
        </Card>
      ) : allLeads.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={Crosshair}
            title="No outbound money trails found"
            description="No transfers left the disclosed accounts to undisclosed destinations."
          />
        </Card>
      ) : (
        <div className="space-y-5">
          {/* Toolbar is rendered identically in every view, so switching the
              filter never shifts the controls down/up the page. */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <ToolbarGroup>
              {FILTERS.map((f) => (
                <ToolbarChip
                  key={f.key}
                  selected={filter === f.key}
                  count={f.count}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </ToolbarChip>
              ))}
            </ToolbarGroup>

            <IconButton
              icon={sortDir === 'desc' ? ArrowDownWideNarrow : ArrowUpNarrowWide}
              variant="secondary"
              onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
              title={sortDir === 'desc' ? 'Sorted by amount: high to low' : 'Sorted by amount: low to high'}
              aria-label={sortDir === 'desc' ? 'Sorted by amount, high to low' : 'Sorted by amount, low to high'}
            />
          </div>

          {filter === 'missing' ? (
            missingDiscoveryLeads.length === 0 ? (
              <Card padding="none">
                <EmptyState size="inline" title="No undisclosed bank or card accounts found." />
              </Card>
            ) : (
              <MissingAccountsTab leads={missingDiscoveryLeads} />
            )
          ) : (
            <>
              {/* The "Net Unaccounted / Leads / Transfers / Income" stats describe
                  money that LEFT the estate — meaningless for internal transfers
                  between the client's own accounts, so hide them in that view. */}
              {filter !== 'internal' && (
                <StatHeader leads={allLeads} income={data?.baseline?.avg_monthly_income} />
              )}
              {data?.created_at && (
                <p className="text-xs text-ink-faint">
                  Last traced {formatTxnDate(data.created_at.slice(0, 10))} · showing {leads.length} of {allLeads.length} lead{allLeads.length !== 1 ? 's' : ''}
                </p>
              )}

              {leads.length === 0 ? (
                <Card padding="none">
                  <EmptyState size="inline" title="No leads match this filter." />
                </Card>
              ) : (
                <div className="space-y-3">
                  {leads.map((lead) => (
                    <LeadRow key={lead.destination_key} lead={lead} onViewSource={setViewer} onRequestStatements={requestStatements} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {viewer && (
        <SourcePageViewer
          statementId={viewer.statementId}
          pageNumber={viewer.pageNumber}
          sourceFile={viewer.sourceFile}
          isOpen={true}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
